<script setup>
  import { ref, computed, onMounted, onUnmounted } from 'vue'
  import { useBackend } from '@/composables/Backend';
  import { useCurrentUser } from '@/composables/CurrentUser';
  import QuoteList from '@/components/QuoteList.vue';

  const backend = useBackend();
  const currentUser = useCurrentUser(handleLoggedIn, handleNotLoggedIn);
  const isLoading = ref(false);
  const error = ref('');

  const showLoginForm = ref(false);
  const showAccountData = ref(false);
  const isRefreshing = ref(false);
  let refreshInterval = null;

  const tabs = {    
    orders: 'orders',
    quotes: 'quotes',
    account: 'account'
  };
  const currentTab = ref(tabs.orders);

  const accountInfo = {
    isLoading: ref(false),
    error: ref(''),
    newFirstName: ref(''),
    newLastName: ref(''),
    newEmail: ref(''),
    doUpdate: () => {
      accountInfo.isLoading.value = true;

      currentUser.doUpdate(
        accountInfo.newFirstName.value, 
        accountInfo.newLastName.value, 
        accountInfo.newEmail.value, 
        (data) => {
          accountInfo.newFirstName.value = data.firstName;
          accountInfo.newLastName.value = data.lastName;
          accountInfo.newEmail.value = data.email;

          accountInfo.isLoading.value = false;
        },
        (error) => { 
          accountInfo.error.value = error
          accountInfo.isLoading.value = false;
        });
    }
  };

  const orders = {
    isLoading: ref(false),
    error: ref(''),
    data: ref([])
  };

  const pageInfo = {
    data: ref([])
  };

  const login = {
    isLoading: ref(false),
    error: ref(''),   
    hasError: computed(() => {
      return login.error.value.length > 0;
    }),
    username: ref(''),
    password: ref(''),
    doLogin: () => {
      login.isLoading.value = true;

      currentUser.doLogin(
        login.username.value, 
        login.password.value, 
        (data) => {
          // data
          accountInfo.newFirstName.value = data.firstName;
          accountInfo.newLastName.value = data.lastName;
          accountInfo.newEmail.value = data.email;

          login.password.value = '';
          login.isLoading.value = false;

          handleLoggedIn(data);
        },
        (error) => {
          login.error.value = error;          
          login.password.value = '';
          login.isLoading.value = false;
        });
    },
    // doLogout: () => {
    //   currentUser.doLogout(() => {
    //     window.location.href = '/';
    //   });
    // }
    doLogout() {
      currentUser.doLogout(
        (redirectUrl) => {
          window.location.href = redirectUrl; // Redirect to Shopify logout
        },
        () => {
          console.error('Logout failed');
          window.location.href = '/'; // Fallback
        }
      );
    }
  }

  async function refreshToken() {
    isRefreshing.value = true;
    try {
      const response = await fetch('/api/refresh-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Token refresh failed');
      }
      console.debug('Token refreshed:', data);
      currentUser.checkLoginStatus();
    } catch (err) {
      console.error('Refresh error:', err);
      window.location.href = '/login';
    } finally {
      isRefreshing.value = false;
    }
  }

  function checkTokenExpiry() {
    const expiresAt = document.cookie
      .split('; ')
      .find(row => row.startsWith('expiresAt='))
      ?.split('=')[1];
    if (expiresAt && new Date(expiresAt * 1000) < new Date()) { // Convert seconds to ms
      refreshToken();
    }
  }

  async function handleLogin() {
    isLoading.value = true;
    error.value = '';
    try {
      backend.login().then(result => {
        if (result == false || result.error) {
          var error = result.error;
          if (!error) {
            error = 'An error occurred communicating with the API.';
          }
          error = `Error updating shopping cart item: ${error}`;        
        }
        else {
          if (result.redirectUrl) {
            window.location.href = result.redirectUrl;
          } else {
            if (error) {
              console.log('No redirect URL returned');
            }
          }
        }
      });
    } catch (err) {
      error.value = err.message;
    } finally {
      isLoading.value = false;
    }
  }

  function handleNotLoggedIn() {
    showLoginForm.value = true;
    showAccountData.value = false;
  }
  
  function handleLoggedIn(data) {
    showLoginForm.value = false;
    showAccountData.value = true;

    // setup customer information change form
    accountInfo.newFirstName.value = data.firstName.value;
    accountInfo.newLastName.value = data.lastName.value;
    accountInfo.newEmail.value = data.email.value;

    // get orders
    orders.isLoading.value = true;
    
    backend.getOrderList(null, 'first')
      .then(ordersResult => {
        if (ordersResult) {
          orders.data.value = ordersResult.orders;
          pageInfo.data.value = ordersResult.pageInfo
          orders.isLoading.value = false;
        }
      });
  }

  // Load next page
  function loadNext() {
    if (pageInfo.data.value.hasNextPage) {
      backend.getOrderList(pageInfo.data.value.endCursor, 'first')
      .then(ordersResult => {
          if (ordersResult) {
            orders.data.value = ordersResult.orders;
            pageInfo.data.value = ordersResult.pageInfo
            orders.isLoading.value = false;
          }
        });
    }
  }

  // Load previous page
  function loadPrevious() {
    if (pageInfo.data.value.hasPreviousPage) {
      backend.getOrderList(pageInfo.data.value.startCursor, 'last')
      .then(ordersResult => {
          if (ordersResult) {
            orders.data.value = ordersResult.orders;
            pageInfo.data.value = ordersResult.pageInfo
            orders.isLoading.value = false;
          }
        });
    }
  }

  onMounted(() => {
    checkTokenExpiry();
    refreshInterval = setInterval(checkTokenExpiry, 60000); // Every minute
  });

  onUnmounted(() => {
    if (refreshInterval) clearInterval(refreshInterval);
  });
</script>

<template>
  <div class="container my-account">
    <div v-if="isRefreshing" class="refreshing">Refreshing your session...</div>
    
    <div v-if="showLoginForm" class="row section--login">
      <div class="col">
        <h2>Welcome back!</h2>
        <div class="dashed-divider"></div>
        <p class="no--margin-b">
            Sign in or sign up today for:
        </p>
        <h3 class="regular no--margin-b">
          - A faster checkout experience<br />
          - Order status and history<br />
          - Easy access to custom quotes<br />
        </h3>
      </div>
      <div class="col">
        <h3>Sign in with Email</h3>
        <button class="button primary__btn col--1-of-4" :disabled="isLoading" @click="handleLogin">
          {{ isLoading ? 'Loading...' : 'Sign In →' }}
        </button>

        <h3 style="margin-top: 24px;">Create an Account</h3>
        <button class="button primary__btn col--1-of-4" :disabled="isLoading" @click="handleLogin">
          {{ isLoading ? 'Loading...' : 'Sign Up →' }}
        </button>
        <div v-if="error" class="alert alert-danger">{{ error }}</div>
      </div>
    </div>

    <div v-if="showAccountData">      
      <div class="section section--pushed">
        <div class="header__title-bar no--border">
          <div class="col-md-8 col-xs-12">
            <h2 class="header__title">Welcome back, {{ currentUser.firstName.value }}!</h2>
          </div>

          <div class="tabs__menu">
            <b-link class="link link__text" @click="login.doLogout" href="#">Log Out →</b-link>
          </div>        
        </div>

        <div class="quotes__nav">
          <div class="quotes__nav-item">
            <a href="#" @click="currentTab = tabs.orders" :class="`nav-link ${currentTab==tabs.orders ? 'active': ''}`">Orders</a>
          </div>
          <div class="quotes__nav-item">
            <a href="#" @click="currentTab = tabs.quotes" :class="`nav-link ${currentTab==tabs.quotes ? 'active': ''}`">Quotes</a>
          </div>
          <div class="quotes__nav-item">
            <a href="#" @click="currentTab = tabs.account" :class="`nav-link ${currentTab==tabs.account ? 'active': ''}`">Account</a>
          </div>
        </div>
      
        <div v-if="currentTab == tabs.orders">
          <div class="tab__content">
            <div class="card-alt card--quote quote" v-for="order in orders.data.value" :key="order.orderId">
              <div class="quotes__col"><label class="pretext">Date</label><span class="ts--callout no--margin-b quote__date">{{ order.purchaseDate }}</span></div>
              <div class="quotes__col"><span class="pretext">Order #</span><span class="ts--callout no--margin-b quote__date">{{ order.orderNumber }}</span></div>
              <div class="quotes__col"><span class="pretext">Total Paid</span><span class="ts--callout no--margin-b quote__date">{{ order.totalCostText }}</span></div>
              <a class="quote_button-view" :href="`/orders/details?id=${order.orderId}`">View Order →</a>
            </div>

            <div v-if="orders.data.value && orders.data.value.length == 0" class="card-alt card--stack">
              <div v-if="orders.isLoading.value">
                <p>Loading...</p>
              </div>
              <div v-else-if="orders.data.value.length == 0">
                <p>There aren't any orders to display.</p>
              </div>
            </div>
          </div>

          <!-- Pagination controls -->
          <div v-if="pageInfo.data.value.hasNextPage || pageInfo.data.value.hasPreviousPage" class="pagination">
            <button
              class="pagination-button button primary__btn"
              :disabled="!pageInfo.data.value.hasPreviousPage"
              @click="loadPrevious"
            >
              Previous
            </button>
            <button
              class="pagination-button button primary__btn"
              :disabled="!pageInfo.data.value.hasNextPage"
              @click="loadNext"
            >
              Next
            </button>
          </div>
        </div>
        
        <div v-if="currentTab == tabs.quotes">          
          <quote-list></quote-list>
        </div>

        <div class="row" v-if="currentTab == tabs.account">
          <div class="col">
            <div class="card card--quote quotes__customer-card">
              <b-form class="quotes__card-form" @submit="accountInfo.doUpdate">
                <h3>Customer Information</h3>
                <div v-if="accountInfo.error.value" class="alert alert-danger">{{ accountInfo.error.value }}</div>
                <b-form-input class="text-input__field labeled" v-model="accountInfo.newFirstName.value" placeholder="First Name"></b-form-input>
                <b-form-input class="text-input__field labeled" v-model="accountInfo.newLastName.value" placeholder="Last Name"></b-form-input>
                <b-form-input class="text-input__field labeled" v-model="accountInfo.newEmail.value" type="email" placeholder="Email" disabled></b-form-input>
                <button data-wait="Please wait..." class="quote_button-view" type="submit">Save Changes →</button>
              </b-form>
            </div>            
          </div>
        </div>
        
      </div>
    </div>
  </div>
</template>